import React from 'react';
import { Users, TrendingUp, ClipboardList, LineChart } from 'lucide-react';
import AuthBackground from './AuthBackground';

const FEATURES = [
  { icon: Users, color: '#3b82f6', bg: '#dbeafe', title: 'Manage Customers', subtitle: 'Build stronger relationships' },
  { icon: TrendingUp, color: '#16a34a', bg: '#dcfce7', title: 'Increase Productivity', subtitle: 'Streamline your processes' },
  { icon: ClipboardList, color: '#7c3aed', bg: '#ede9fe', title: 'Stay Organized', subtitle: 'Everything at your fingertips' },
  { icon: LineChart, color: '#ea580c', bg: '#ffedd5', title: 'Grow Your Business', subtitle: 'Turn opportunities into success' }
];

// Page frame shared by the sign-in and two-step verification pages:
// decorative background, brand/marketing panel on the left, and the page's
// own card (children) on the right. Below 900px only the card is shown.
const AuthLayout = ({ children }) => (
  <div className="login-page">
    <style>{`
      .login-page {
        position: relative;
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2rem;
        overflow: hidden;
        font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        background: #f7f9ff;
      }
      .login-shell {
        position: relative;
        width: 100%;
        max-width: 1180px;
        display: grid;
        grid-template-columns: 1fr 440px;
        gap: 3rem;
        align-items: center;
        z-index: 1;
      }
      .login-brand-panel { position: relative; color: #111827; }
      .login-feature-row {
        display: flex;
        align-items: center;
        gap: 0.9rem;
        margin-bottom: 1.1rem;
      }

      /* Animated gradient fill for the "CRM" wordmark accent. */
      .login-gradient-text {
        background: linear-gradient(90deg, #1d4ed8, #60a5fa, #1d4ed8);
        background-size: 200% auto;
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
        animation: auth-shine 6s ease-in-out infinite;
      }
      @keyframes auth-shine {
        0%, 100% { background-position: 0% center; }
        50% { background-position: 100% center; }
      }
      @media (prefers-reduced-motion: reduce) {
        .login-gradient-text { animation: none; color: #2563eb; }
      }

      /* Feature row icon badges lift and tilt playfully on hover. */
      .login-feature-icon {
        transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s ease;
      }
      .login-feature-row:hover .login-feature-icon {
        transform: translateY(-3px) rotate(-6deg) scale(1.07);
      }

      @media (max-width: 900px) {
        .login-shell {
          grid-template-columns: 1fr;
          max-width: 440px;
        }
        .login-brand-panel {
          display: none;
        }
      }
      @media (max-width: 480px) {
        .login-page { padding: 1rem; }
      }
    `}</style>

    <AuthBackground />

    <div className="login-shell">
      {/* Brand / marketing panel */}
      <div className="login-brand-panel">
        <div style={{ marginBottom: '2.5rem' }}>
          <img src="/wi-logo.svg" alt="Webindia - complete business solutions" style={{ height: '52px', width: 'auto' }} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem' }}>
          <span style={{ width: '28px', height: '2px', background: '#2563eb', display: 'inline-block' }} />
          <span style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.12em', color: '#2563eb' }}>WELCOME TO</span>
        </div>

        <h1 style={{ fontSize: '2.75rem', fontWeight: 800, color: '#0f172a', margin: '0 0 1rem', lineHeight: 1.15 }}>
          Webindia <span className="login-gradient-text">CRM</span>
        </h1>

        <p style={{ fontSize: '1.05rem', color: '#475569', maxWidth: '440px', marginBottom: '2.25rem' }}>
          Manage your customers, sales, and projects, all in one place.
        </p>

        <div>
          {FEATURES.map(({ icon: Icon, color, bg, title, subtitle }) => (
            <div className="login-feature-row" key={title}>
              <div className="login-feature-icon" style={{
                width: '46px', height: '46px', borderRadius: '50%',
                backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, border: `1px solid ${color}22`
              }}>
                <Icon size={22} color={color} />
              </div>
              <div>
                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '1rem' }}>{title}</div>
                <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{subtitle}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: '2rem', fontSize: '0.85rem', fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <span style={{ width: '24px', height: '2px', background: '#94a3b8', display: 'inline-block' }} />
          Simpler &nbsp;|&nbsp; Smarter &nbsp;|&nbsp; Stronger
        </div>
      </div>

      {children}
    </div>
  </div>
);

export default AuthLayout;
